<template>
    <LoadingCard v-if="loading"></LoadingCard>
    <div class="card" v-else>
        <div class="bg-primary align-items-center d-flex">
            <h5 class="card-header text-white">{{ user.name }} Blocks</h5>
            <b-pagination align="right" class="my-auto mr-2 ml-auto" :total-rows="pagination.total" v-model="pagination.page"
                          :per-page="pagination.limit" />
        </div>

        <div class="form-control form-control-lg bg-light form-filter d-flex">
            <VueSelect
                    v-model="cropType"
                    :options="allCropTypes"
                    placeholder="Filter by CropType"
                    label="name"
                    track-by="id"
            ></VueSelect>
            <VueSelect
                    v-model="variety"
                    :options="allVarieties"
                    placeholder="Filter by Variety"
                    label="name"
                    track-by="id"
            ></VueSelect>
        </div>

        <b-table hover stacked="md" :items="tableData" :fields="blocksTable.fields"
                 :per-page="pagination.limit" :current-page="pagination.page"
                 :show-empty="true">

            <template slot="cropType" slot-scope="row">
                <span v-if="row.item.cropType">{{ row.item.cropType.name }}</span>
                <span class="badge badge-danger" v-else>Removed</span>
            </template>

            <template slot="variety" slot-scope="row">
                <span v-if="row.item.cropType && row.item.variety">{{ row.item.variety.name }}</span>
                <span class="badge badge-danger" v-else-if="row.item.cropType">Removed</span>
                <span class="text-muted" v-else>(none)</span>
            </template>

            <template slot="withholdingPeriods" slot-scope="row">
                <template v-if="row.item.currentWithholdingPeriods.length > 0">
                    <span class="badge-group mr-2" v-for="period in activeWithholdingPeriods(row.item)">
                        <span class="badge badge-secondary mb-1">{{ period.country.name }} ({{ period.length }} days)</span>
                        <span class="badge mb-1" :class="{'badge-success': period.is_past, 'badge-danger': !period.is_past}">{{ period.ends_at | date_format }}</span>
                    </span>
                </template>
                <span class="text-muted" v-else>(none)</span>
            </template>

            <template slot="restrictedEntryInterval" slot-scope="row">
                <strong class="text-danger" v-if="row.item.restrictedEntryInterval">{{ row.item.restrictedEntryInterval.ends_at | date_format
                                                                                    }}</strong>
                <span class="text-muted" v-else>(none)</span>
            </template>
            <template slot="actions" slot-scope="row">
                <b-button :to="{name: 'spray-diary.view', params: {blockId: row.item.id}}"
                          variant="secondary btn--icon"
                          v-b-tooltip="'View spray diary'">
                    <i class="zmdi zmdi-view-list-alt"></i>
                </b-button>

            </template>
        </b-table>
    </div>

</template>

<script>
import findUserQuery from '@views/GrowerBlocks/ProcessorBrowse/queryFindUser.graphql';
import listBlocksQuery from '@views/GrowerBlocks/ProcessorBrowse/queryListBlocks.graphql';
import listCropTypesQuery from '@views/GrowerBlocks/ProcessorBrowse/queryListCropTypes.graphql';
import listVarietiesQuery from '@views/GrowerBlocks/ProcessorBrowse/queryListVarieties.graphql';

import isAfter from 'date-fns/is_after';

export default {
  name: 'ProcessorGrowerBlocksBrowse',
  props: ['growerId'],
  components: {},
  apollo: {
    user: {
      query: findUserQuery,
      update: data => data.findUser,
      variables() {
        return {
          input: {
            id: this.growerId,
          },
        };
      },
    },
    blocks: {
      query: listBlocksQuery,
      variables() {
        const variables = {
          input: {
            user_id: this.growerId,
          },
        };
        if (this.variety) {
          variables.input.variety_id = this.variety.id;
        }
        if (this.cropType) {
          variables.input.crop_type_id = this.cropType.id;
        }

        return variables;
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          this.blocks = data.filterBlocks.data;
          this.pagination.total = data.filterBlocks.meta.total;
        }
      },
    },
    allCropTypes: {
      query: listCropTypesQuery,
      update: data => data.filterCropTypes.data,
    },
    allVarieties: {
      query: listVarietiesQuery,
      update: data =>  data.filterVarieties.data,
    },
  },
  data() {
    return {
      loading: 0,
      blocks: [],
      allCropTypes: [],
      allVarieties: [],
      variety: null,
      cropType: null,
      pagination: {
        page: 1,
        limit: this.$tableDefaultPageSize,
        total: 0,
      },
      blocksTable: {
        fields: [
          { key: 'name', label: 'Block Name', sortable: true },
          { key: 'withholdingPeriods', label: 'Withholding Periods' },
          { key: 'restrictedEntryInterval', label: 'REI' },
          {
            key: 'cropType',
            label: 'Crop Type',
            sortable: true,
          },
          {
            key: 'variety',
            label: 'Crop Variety',
            sortable: true,
            formatter: variety => variety.name,
          },
          { key: 'actions', label: '', class: 'text-right' },
        ],
      },
    };
  },
  computed: {

    tableData() {
      return (this.blocks || []).map(item => ({
        ...item,
        _cellVariants: {restrictedEntryInterval: item.restrictedEntryInterval ? 'danger' : null},
        // _rowVariant: item.restrictedEntryInterval ? 'danger' : null,
      }));
    },
  },
  methods: {
    activeWithholdingPeriods(item) {
      const byCountry = {};
      item.currentWithholdingPeriods.forEach(period => {
        if (!(period.country.id in byCountry) || isAfter(period.ends_at, byCountry[period.country.id].ends_at)) {
          byCountry[period.country.id] = period;
        }
      });

      return Object.values(byCountry);
    },
  },
};
</script>
